import type { IntlShape } from "@formatjs/intl";
import { Badge, Card, Flex, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import type { Throughput } from "../../generated-axios-client";
import { palette } from "../../theme";
import styles from "../Dashboard.module.css";

type Props = {
  intl: IntlShape;
  throughput: Throughput | undefined;
  loading: boolean;
};

const CHART_HEIGHT = 160;
const LABEL_HEIGHT = 20;
const WEEK_WIDTH = 48;
const BAR_WIDTH = 14;
const BAR_GAP = 4;

const CREATED_COLOR = palette.dashBlue;
const FINISHED_COLOR = palette.dashGreen;

export const ThroughputChart = ({ intl, throughput, loading }: Props) => {
  const weeks = throughput?.weeks ?? [];
  const highest = Math.max(1, ...weeks.flatMap((week) => [week.created, week.finished]));
  const barHeight = (count: number) => (count / highest) * (CHART_HEIGHT - LABEL_HEIGHT);
  const chartWidth = weeks.length * WEEK_WIDTH;

  const averageDays = throughput?.average_days_to_finish;

  return (
    <Card
      className={styles.fullHeight}
      title={intl.formatMessage({ id: "dashboard_throughput_title" })}
      loading={loading}
      extra={
        averageDays != null && (
          <Typography.Text type="secondary">
            {intl.formatMessage(
              { id: "dashboard_throughput_average" },
              { days: averageDays }
            )}
          </Typography.Text>
        )
      }
    >
      <Flex vertical gap="middle">
        <Flex gap="middle">
          <Badge
            color={CREATED_COLOR}
            text={intl.formatMessage({ id: "dashboard_throughput_created" })}
          />
          <Badge
            color={FINISHED_COLOR}
            text={intl.formatMessage({ id: "dashboard_throughput_finished" })}
          />
        </Flex>
        <svg
          className={styles.throughputChart}
          viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
          role="img"
          aria-label={intl.formatMessage({ id: "dashboard_throughput_title" })}
        >
          {weeks.map((week, index) => {
            const weekLeft =
              index * WEEK_WIDTH + (WEEK_WIDTH - BAR_WIDTH * 2 - BAR_GAP) / 2;
            const baseline = CHART_HEIGHT - LABEL_HEIGHT;
            const weekLabel = intl.formatMessage(
              { id: "dashboard_throughput_week_tooltip" },
              {
                week: intl.formatDate(dayjs(week.week_start).toDate(), {
                  day: "2-digit",
                  month: "2-digit",
                }),
                created: week.created,
                finished: week.finished,
              }
            );
            return (
              <Tooltip key={week.week_start} title={weekLabel}>
                <g>
                  <rect
                    x={index * WEEK_WIDTH}
                    y={0}
                    width={WEEK_WIDTH}
                    height={CHART_HEIGHT}
                    fill="transparent"
                  />
                  <rect
                    x={weekLeft}
                    y={baseline - barHeight(week.created)}
                    width={BAR_WIDTH}
                    height={barHeight(week.created)}
                    fill={CREATED_COLOR}
                    rx={2}
                  />
                  <rect
                    x={weekLeft + BAR_WIDTH + BAR_GAP}
                    y={baseline - barHeight(week.finished)}
                    width={BAR_WIDTH}
                    height={barHeight(week.finished)}
                    fill={FINISHED_COLOR}
                    rx={2}
                  />
                  {/* every other week is labelled, ending with the current one */}
                  {index % 2 === (weeks.length - 1) % 2 && (
                    <text
                      className={styles.throughputLabel}
                      x={index * WEEK_WIDTH + WEEK_WIDTH / 2}
                      y={CHART_HEIGHT - 4}
                      textAnchor="middle"
                    >
                      {intl.formatDate(dayjs(week.week_start).toDate(), {
                        day: "2-digit",
                        month: "2-digit",
                      })}
                    </text>
                  )}
                </g>
              </Tooltip>
            );
          })}
        </svg>
      </Flex>
    </Card>
  );
};
